// Canvas 渲染器:地块 / 建筑 / 角色 / 天气 / 日夜 / 气泡
import { T, type AgentSnapshot, type Building } from "../../shared/types";
import { TILE, tileSprite, charSprite, drawBuilding, roundRect } from "./sprites";

interface HomeSpot { id: string; x: number; y: number; doorX: number; doorY: number }

export interface RenderState {
  tiles: number[];
  width: number;
  height: number;
  agents: AgentSnapshot[];
  buildings: Building[];
  homeSpots: HomeSpot[];
  gameMin: number;
  weather: string;
  myAgentId: string | null;
  selectedId: string | null;
}

export class TownRenderer {
  private canvas: HTMLCanvasElement;
  private g: CanvasRenderingContext2D;
  camX = 26 * TILE;
  camY = 26 * TILE;
  zoom = 1;
  private dragging = false;
  private dragStart = { x: 0, y: 0, camX: 0, camY: 0 };
  private moved = 0;
  onTileClick: ((x: number, y: number, agentId: string | null) => void) | null = null;
  followMyAgent = true;
  private rainDrops: { x: number; y: number; v: number }[] = [];

  constructor(canvas: HTMLCanvasElement) {
    this.canvas = canvas;
    this.g = canvas.getContext("2d")!;
    this.bindEvents();
    for (let i = 0; i < 90; i++) {
      this.rainDrops.push({ x: Math.random() * 2000, y: Math.random() * 1200, v: 6 + Math.random() * 4 });
    }
  }

  resize() {
    const dpr = window.devicePixelRatio || 1;
    this.canvas.width = this.canvas.clientWidth * dpr;
    this.canvas.height = this.canvas.clientHeight * dpr;
  }

  private bindEvents() {
    const c = this.canvas;
    c.addEventListener("mousedown", (e) => {
      this.dragging = true;
      this.moved = 0;
      this.dragStart = { x: e.clientX, y: e.clientY, camX: this.camX, camY: this.camY };
    });
    window.addEventListener("mousemove", (e) => {
      if (!this.dragging) return;
      const dx = e.clientX - this.dragStart.x, dy = e.clientY - this.dragStart.y;
      this.moved += Math.abs(dx) + Math.abs(dy);
      if (this.moved > 8) this.followMyAgent = false;
      this.camX = this.dragStart.camX - dx / this.zoom;
      this.camY = this.dragStart.camY - dy / this.zoom;
    });
    window.addEventListener("mouseup", () => (this.dragging = false));
    c.addEventListener("click", (e) => {
      if (this.moved > 8 || !this.onTileClick) return;
      const rect = c.getBoundingClientRect();
      const wx = (e.clientX - rect.left - rect.width / 2) / this.zoom + this.camX;
      const wy = (e.clientY - rect.top - rect.height / 2) / this.zoom + this.camY;
      this.onTileClick(wx / TILE, wy / TILE, null);
    });
    c.addEventListener("wheel", (e) => {
      e.preventDefault();
      this.zoom = Math.min(2.2, Math.max(0.5, this.zoom * (e.deltaY > 0 ? 0.9 : 1.1)));
    }, { passive: false });
    // 触屏
    let lastTouch: { x: number; y: number } | null = null;
    c.addEventListener("touchstart", (e) => {
      const t = e.touches[0];
      lastTouch = { x: t.clientX, y: t.clientY };
      this.dragStart = { x: t.clientX, y: t.clientY, camX: this.camX, camY: this.camY };
      this.moved = 0;
    });
    c.addEventListener("touchmove", (e) => {
      if (!lastTouch) return;
      const t = e.touches[0];
      const dx = t.clientX - this.dragStart.x, dy = t.clientY - this.dragStart.y;
      this.moved += Math.abs(t.clientX - lastTouch.x) + Math.abs(t.clientY - lastTouch.y);
      if (this.moved > 10) this.followMyAgent = false;
      this.camX = this.dragStart.camX - dx / this.zoom;
      this.camY = this.dragStart.camY - dy / this.zoom;
      e.preventDefault();
    }, { passive: false });
    c.addEventListener("touchend", (e) => {
      if (this.moved <= 10 && this.onTileClick) {
        const t = e.changedTouches[0];
        const rect = c.getBoundingClientRect();
        const wx = (t.clientX - rect.left - rect.width / 2) / this.zoom + this.camX;
        const wy = (t.clientY - rect.top - rect.height / 2) / this.zoom + this.camY;
        this.onTileClick(wx / TILE, wy / TILE, null);
      }
      lastTouch = null;
    });
  }

  screenToWorld(sx: number, sy: number) {
    const rect = this.canvas.getBoundingClientRect();
    return {
      x: (sx - rect.left - rect.width / 2) / this.zoom + this.camX,
      y: (sy - rect.top - rect.height / 2) / this.zoom + this.camY,
    };
  }

  render(s: RenderState, time: number) {
    const g = this.g;
    const W = this.canvas.width, H = this.canvas.height;
    const dpr = window.devicePixelRatio || 1;
    g.save();
    g.scale(dpr, dpr);
    const vw = W / dpr, vh = H / dpr;

    // 摄像机跟随
    if (this.followMyAgent && s.myAgentId) {
      const me = s.agents.find((a) => a.id === s.myAgentId);
      if (me) {
        this.camX += (me.x * TILE + TILE / 2 - this.camX) * 0.08;
        this.camY += (me.y * TILE + TILE / 2 - this.camY) * 0.08;
      }
    }
    this.camX = Math.max(vw / 2 / this.zoom - TILE, Math.min(s.width * TILE - vw / 2 / this.zoom + TILE, this.camX));
    this.camY = Math.max(vh / 2 / this.zoom - TILE, Math.min(s.height * TILE - vh / 2 / this.zoom + TILE, this.camY));

    // 背景
    const night = nightAlpha(s.gameMin);
    g.fillStyle = night > 0.4 ? "#0e1530" : "#5da03e";
    g.fillRect(0, 0, vw, vh);

    g.translate(vw / 2, vh / 2);
    g.scale(this.zoom, this.zoom);
    g.translate(-this.camX, -this.camY);

    // 可视范围
    const x0 = Math.max(0, Math.floor((this.camX - vw / 2 / this.zoom) / TILE) - 1);
    const y0 = Math.max(0, Math.floor((this.camY - vh / 2 / this.zoom) / TILE) - 1);
    const x1 = Math.min(s.width - 1, Math.ceil((this.camX + vw / 2 / this.zoom) / TILE) + 1);
    const y1 = Math.min(s.height - 1, Math.ceil((this.camY + vh / 2 / this.zoom) / TILE) + 1);

    // 地块
    for (let y = y0; y <= y1; y++) {
      for (let x = x0; x <= x1; x++) {
        const t = s.tiles[y * s.width + x];
        if (t === T.WALL) {
          g.drawImage(tileSprite(T.GRASS, x, y), x * TILE, y * TILE);
          continue;
        }
        if (t === T.DOOR) {
          g.drawImage(tileSprite(T.PATH, x, y), x * TILE, y * TILE);
          continue;
        }
        g.drawImage(tileSprite(t, x, y), x * TILE, y * TILE);
      }
    }

    // 建筑
    for (const b of s.buildings) {
      if (!b.open) drawBuilding(g, b);
    }
    for (const h of s.homeSpots) {
      drawBuilding(g, { x: h.x, y: h.y, w: 3, h: 3, color: "#c084fc", name: "", emoji: "", open: false }, true);
    }

    // 水面/篝火动画
    if (Math.floor(time / 600) % 2 === 0) {
      g.fillStyle = "rgba(255,255,255,0.18)";
      for (let y = y0; y <= y1; y++) {
        for (let x = x0; x <= x1; x++) {
          if (s.tiles[y * s.width + x] === T.WATER && (x + y) % 3 === 0) {
            g.fillRect(x * TILE + 6, y * TILE + 14, 14, 2);
          }
        }
      }
    }

    // 角色(按 y 排序);LOD:视野外不绘制,距中心较远的角色省略名字/气泡,降低大量角色时的绘制开销
    const camXg = this.camX / TILE, camYg = this.camY / TILE;
    const halfW = vw / 2 / this.zoom / TILE;
    const halfH = vh / 2 / this.zoom / TILE;
    const visible = (a: AgentSnapshot) => Math.abs(a.x - camXg) <= halfW + 1 && Math.abs(a.y - camYg) <= halfH + 1;
    const distSq = (a: AgentSnapshot) => (a.x - camXg) ** 2 + (a.y - camYg) ** 2;
    const nameFarSq = (Math.max(halfW, halfH) * 0.72) ** 2;
    const sorted = s.agents.filter(visible).sort((a, b) => a.y - b.y);

    // 气泡布局:同格角色垂直堆叠,跨格重叠时水平错开(最上层气泡保持居中)
    interface BubbleRect { a: AgentSnapshot; bx: number; by: number; tw: number; th: number; text: string }
    const bubbles: BubbleRect[] = [];
    const sameTile = new Map<string, number>();
    for (const a of sorted) {
      if (!a.bubble || distSq(a) > nameFarSq) continue;
      const k = `${Math.round(a.x)},${Math.round(a.y)}`;
      const n = sameTile.get(k) ?? 0;
      sameTile.set(k, n + 1);
      g.font = "11px sans-serif";
      const tw = Math.min(g.measureText(a.bubble).width + 14, 220);
      bubbles.push({
        a,
        bx: a.x * TILE + 16 - tw / 2,
        by: a.y * TILE - 52 - n * 26,
        tw,
        th: 22,
        text: a.bubble.length > 18 ? a.bubble.slice(0, 18) + "…" : a.bubble,
      });
    }
    const placedBubbles: { bx: number; by: number; tw: number; th: number }[] = [];
    const bubbleOverlap = (bx: number, by: number, tw: number, th: number) =>
      placedBubbles.some((p) => by < p.by + p.th - 4 && by + th > p.by + 4 && bx < p.bx + p.tw - 4 && bx + tw > p.bx + 4);
    for (const bub of [...bubbles].reverse()) {
      let bx = bub.bx;
      if (bubbleOverlap(bx, bub.by, bub.tw, bub.th)) {
        let found = false;
        for (let k = 1; k <= 9 && !found; k++) {
          const nx = bub.bx + k * 24;
          if (Math.abs(nx - bub.bx) <= 7 * TILE && !bubbleOverlap(nx, bub.by, bub.tw, bub.th)) { bx = nx; found = true; }
        }
        for (let k = 1; k <= 9 && !found; k++) {
          const nx = bub.bx - k * 24;
          if (Math.abs(nx - bub.bx) <= 7 * TILE && !bubbleOverlap(nx, bub.by, bub.tw, bub.th)) { bx = nx; found = true; }
        }
        if (!found) bx = bub.bx;
      }
      placedBubbles.push({ bx, by: bub.by, tw: bub.tw, th: bub.th });
      bub.bx = bx;
    }

    for (const a of sorted) {
      const frame = a.moving ? (Math.floor(time / 180) % 2) : 0;
      const cx = a.x * TILE + TILE / 2 - 16;
      const cy = a.y * TILE + TILE / 2 - 24;
      g.drawImage(charSprite(a.color, a.hat, frame), cx, cy);

      // 名字(远处省略)
      const far = distSq(a) > nameFarSq;
      if (!far) {
        g.font = "10px sans-serif";
        g.textAlign = "center";
        const label = `${a.emoji} ${a.name}`;
        const lw = g.measureText(label).width + 8;
        g.fillStyle = a.isPlayer ? "rgba(250,204,21,0.92)" : "rgba(255,255,255,0.85)";
        roundRect(g, a.x * TILE + 16 - lw / 2, a.y * TILE - 26, lw, 14, 4);
        g.fill();
        g.fillStyle = "#3a2e28";
        g.fillText(label, a.x * TILE + 16, a.y * TILE - 16);
      }

      // 选中圈
      if (a.id === s.selectedId) {
        g.strokeStyle = "#facc15";
        g.lineWidth = 2;
        g.beginPath();
        g.ellipse(a.x * TILE + 16, a.y * TILE + 26, 14, 6, 0, 0, Math.PI * 2);
        g.stroke();
      }

    }

    // 气泡(最后统一绘制,保证在最上层;尾巴连线指向对应角色)
    for (const bub of bubbles) {
      const tailX = bub.bx + bub.tw / 2;
      g.font = "11px sans-serif";
      g.fillStyle = "rgba(255,255,255,0.96)";
      roundRect(g, bub.bx, bub.by, bub.tw, bub.th, 8);
      g.fill();
      g.strokeStyle = "rgba(255,255,255,0.9)";
      g.lineWidth = 1.5;
      g.beginPath();
      g.moveTo(tailX, bub.by + bub.th);
      g.lineTo(bub.a.x * TILE + 16, bub.a.y * TILE - 24);
      g.stroke();
      g.fillStyle = "rgba(255,255,255,0.96)";
      g.beginPath();
      g.moveTo(tailX - 5, bub.by + bub.th);
      g.lineTo(tailX + 5, bub.by + bub.th);
      g.lineTo(tailX, bub.by + bub.th + 6);
      g.closePath();
      g.fill();
      g.fillStyle = "#3a2e28";
      g.fillText(bub.text, tailX, bub.by + 15);
    }

    // 夜色
    if (night > 0.02) {
      g.fillStyle = `rgba(10, 16, 48, ${night * 0.55})`;
      g.fillRect(this.camX - vw / this.zoom, this.camY - vh / this.zoom, vw * 2 / this.zoom, vh * 2 / this.zoom);
      // 建筑暖光
      if (night > 0.3) {
        g.fillStyle = "rgba(255, 200, 90, 0.12)";
        for (const b of s.buildings) {
          if (!b.open) {
            g.beginPath();
            g.ellipse((b.x + b.w / 2) * TILE, (b.y + b.h) * TILE, b.w * TILE * 0.7, 40, 0, 0, Math.PI * 2);
            g.fill();
          }
        }
      }
    }

    // 雨
    if (s.weather === "rain") {
      g.strokeStyle = "rgba(180, 210, 255, 0.5)";
      g.lineWidth = 1;
      for (const d of this.rainDrops) {
        d.y += d.v;
        if (d.y > this.camY + vh / this.zoom) {
          d.y = this.camY - vh / this.zoom;
          d.x = this.camX - vw / this.zoom + Math.random() * (vw * 2 / this.zoom);
        }
        g.beginPath();
        g.moveTo(d.x, d.y);
        g.lineTo(d.x - 2, d.y + 10);
        g.stroke();
      }
    }

    g.restore();
  }
}

// 夜晚强度 0-1
function nightAlpha(gameMin: number) {
  const h = gameMin / 60;
  if (h >= 7 && h < 17.5) return 0;
  if (h >= 17.5 && h < 20) return (h - 17.5) / 2.5 * 0.85;
  if (h >= 20 || h < 5) return 0.85;
  if (h >= 5 && h < 7) return 0.85 * (1 - (h - 5) / 2);
  return 0;
}
